import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { useSubsheets } from "@/hooks/useSubsheets";
import { useBacklogItems } from "@/hooks/useBacklogItems";
import { useSelectedSubsheet } from "@/hooks/useSelectedSubsheet";
import { Home, List, Users, Activity, Bell, Hash } from "lucide-react";

const pages = [
  { name: "Dashboard", path: "/", icon: Home },
  { name: "Backlog", path: "/backlog", icon: List },
  { name: "People", path: "/people", icon: Users },
  { name: "Activity", path: "/activity", icon: Activity },
  { name: "Catch Up", path: "/mentions", icon: Bell },
];

export const CommandPalette = () => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const { data: subsheets = [] } = useSubsheets();
  const { data: backlogItems = [] } = useBacklogItems();
  const { setSelectedSubsheetId } = useSelectedSubsheet();

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    // Allow other UI (e.g. the top-nav search button) to open the palette.
    const openPalette = () => setOpen(true);

    document.addEventListener("keydown", down);
    window.addEventListener("open-command-palette", openPalette);
    return () => {
      document.removeEventListener("keydown", down);
      window.removeEventListener("open-command-palette", openPalette);
    };
  }, []);

  const handleSelect = useCallback(
    (path: string) => {
      setOpen(false);
      setSearch("");
      navigate(path);
    },
    [navigate],
  );

  const handleSubsheetSelect = useCallback(
    (subsheetId: string) => {
      setOpen(false);
      setSearch("");
      setSelectedSubsheetId(subsheetId);
      navigate("/backlog");
    },
    [navigate, setSelectedSubsheetId],
  );

  const filteredItems = backlogItems
    .filter(
      (item) => item.task?.toLowerCase().includes(search.toLowerCase()) || String(item.ticket_number).includes(search),
    )
    .slice(0, 10);

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Type a command or search..." value={search} onValueChange={setSearch} />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>

        <CommandGroup heading="Pages">
          {pages.map((page) => (
            <CommandItem key={page.path} onSelect={() => handleSelect(page.path)}>
              <page.icon className="mr-2 h-4 w-4" />
              {page.name}
            </CommandItem>
          ))}
        </CommandGroup>

        {subsheets.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Departments">
              {subsheets.map((subsheet) => (
                <CommandItem key={subsheet.id} onSelect={() => handleSubsheetSelect(subsheet.id)}>
                  <Hash className="mr-2 h-4 w-4" />
                  {subsheet.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {search.length > 0 && filteredItems.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Backlog Items">
              {filteredItems.map((item) => (
                <CommandItem key={item.id} onSelect={() => handleSelect(`/item/${item.ticket_number}`)}>
                  <List className="mr-2 h-4 w-4" />#{item.ticket_number} {item.task}
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
      </CommandList>
    </CommandDialog>
  );
};
